import { Injectable, signal } from '@angular/core';

/**
 * Serviço global de loading.
 *
 * Uso em TypeScript:
 * ```ts
 * private loading = inject(LoadingService);
 *
 * this.loading.show();
 * this.loading.show('Salvando...');
 * this.loading.hide();
 * ```
 *
 * Para usar o overlay global, adicione `<app-loading />` (sem @Input) em app.html.
 * O componente reagirá automaticamente a este serviço.
 */
@Injectable({ providedIn: 'root' })
export class LoadingService {
  private readonly _visible = signal(false);
  private readonly _message = signal('Aguarde...');
  private _count = 0;

  /** Estado reativo de visibilidade (readonly). */
  readonly visible = this._visible.asReadonly();

  /** Mensagem reativa (readonly). */
  readonly message = this._message.asReadonly();

  /**
   * Exibe o loading. Chamadas aninhadas são contadas —
   * o overlay só some quando `hide()` for chamado o mesmo número de vezes.
   */
  show(message = 'Aguarde...'): void {
    this._count++;
    this._message.set(message);
    this._visible.set(true);
  }

  /** Oculta o loading. Se houver chamadas aninhadas, só oculta na última. */
  hide(): void {
    this._count = Math.max(0, this._count - 1);
    if (this._count === 0) {
      this._visible.set(false);
    }
  }

  /** Força o reset completo independente de chamadas aninhadas. */
  reset(): void {
    this._count = 0;
    this._visible.set(false);
  }
}
